import { useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { Link } from 'react-router-dom';
import { testimonials } from '@/mocks/comics';

const FORM_SUBMIT_ADDR = 'http://xudongprc.com/';

const platformStats = [
  { value: '5000+', label: '优质漫画', icon: 'ri-book-open-line' },
  { value: '320万+', label: '日活用户', icon: 'ri-user-line' },
  { value: '98.6%', label: '用户好评率', icon: 'ri-thumb-up-line' },
  { value: '15分钟', label: '平均日阅读时长', icon: 'ri-time-line' },
];

const milestones = [
  { year: '2024年3月', title: '平台上线', desc: '古风漫画网正式上线，首批上线500部精选韩漫' },
  { year: '2024年6月', title: '用户破百万', desc: '上线三个月注册用户突破100万，日活用户超30万' },
  { year: '2024年9月', title: '内容扩展', desc: '新增宫斗权谋、轻松搞笑等分类，漫画总数突破2000部' },
  { year: '2025年1月', title: '阅读体验升级', desc: '全新下拉式阅读引擎上线，加载速度提升60%' },
  { year: '2025年6月', title: '日活破300万', desc: '成为国内领先的韩漫阅读平台，日活跃用户突破300万' },
  { year: '2025年12月', title: '品牌升级', desc: '全站改版，以古风美学重塑品牌视觉，用户留存率提升40%' },
];

export default function About() {
  const [formSuccess, setFormSuccess] = useState(false);
  const [formError, setFormError] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setFormError('');
    const form = e.currentTarget;
    const formData = new FormData(form);
    const honeypot = (formData.get('company_alt') as string || '').trim();
    if (honeypot) {
      setFormSuccess(true);
      return;
    }
    try {
      const res = await fetch(FORM_SUBMIT_ADDR, { method: 'POST', body: formData });
      const text = await res.text();
      let parsed: any = {};
      try { parsed = JSON.parse(text); } catch { /* ignore */ }
      if (res.ok && parsed?.code === 'OK') {
        setFormSuccess(true);
        form.reset();
      } else {
        const errMsg = parsed?.meta?.message || parsed?.message || '提交失败，请稍后再试';
        setFormError(errMsg);
      }
    } catch {
      setFormError('网络错误，请检查连接后重试');
    }
  };

  // Breadcrumb LD
  const breadcrumbLD = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: '首页', item: 'http://xudongprc.com/' },
      { '@type': 'ListItem', position: 2, name: '关于我们' },
    ],
  };

  // Organization LD
  const organizationLD = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: '古风漫画网',
    url: 'http://xudongprc.com/',
    description: '古风漫画网是国内领先的韩国漫画在线阅读平台，致力于为中国读者提供海量优质的韩国漫画资源。',
    foundingDate: '2024-03-01',
    contactPoint: {
      '@type': 'ContactPoint',
      telephone: '+86-400-888-6666',
      contactType: 'customer service',
      email: 'hello@xudongprc.com',
    },
    address: {
      '@type': 'PostalAddress',
      streetAddress: '文三路478号华星时代广场15层',
      addressLocality: '杭州市',
      addressRegion: '浙江省',
      addressCountry: 'CN',
      postalCode: '310000',
    },
  };

  // FAQ LD
  const faqLD = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [
      {
        '@type': 'Question',
        name: '古风漫画网是免费的吗？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '是的，古风漫画网上的所有韩国漫画均为免费在线阅读，无需付费即可畅享海量精品内容。',
        },
      },
      {
        '@type': 'Question',
        name: '需要下载APP才能看漫画吗？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '不需要下载任何APP。古风漫画网适配电脑、平板、手机全终端，只需使用浏览器即可流畅阅读，特别为移动端优化了下拉式阅读体验。',
        },
      },
      {
        '@type': 'Question',
        name: '漫画更新频率如何？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '我们每日同步韩网更新，热门作品基本实现实时连载。每周新增超过200话新内容，确保读者追更零时差。',
        },
      },
    ],
  };

  return (
    <>
      <Navbar />
      <main className="pt-20 md:pt-24 min-h-screen bg-background-50">
        {/* Structured data */}
        <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbLD) }} />
        <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(organizationLD) }} />
        <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(faqLD) }} />

        {/* Breadcrumb */}
        <div className="px-4 md:px-6 max-w-7xl mx-auto pt-8">
          <nav className="flex items-center gap-2 text-sm text-foreground-500" aria-label="面包屑导航">
            <Link to="/" className="hover:text-primary-500 transition-colors">首页</Link>
            <i className="ri-arrow-right-s-line text-xs"></i>
            <span className="text-foreground-800 font-medium">关于我们</span>
          </nav>
        </div>

        {/* Hero */}
        <section className="px-4 md:px-6 max-w-7xl mx-auto py-14 md:py-20">
          <div className="max-w-3xl">
            <span className="text-xs text-secondary-600 font-medium tracking-widest uppercase">关于古风</span>
            <h1 className="text-4xl md:text-5xl font-bold text-foreground-950 font-heading mt-4 leading-tight">
              以古风之美<br />
              诠释韩漫新境界
            </h1>
            <p className="text-foreground-600 mt-6 leading-relaxed text-base">
              古风漫画网创立于2024年，是国内领先的韩国漫画在线阅读平台。我们致力于为中国读者提供海量优质的韩国漫画资源，以独特的古风审美视角甄选每一部作品，让读者在享受流畅阅读体验的同时，感受东方美学的独特魅力。
            </p>
          </div>
        </section>

        {/* Platform stats */}
        <section className="bg-background-100 py-12 md:py-16">
          <div className="px-4 md:px-6 max-w-7xl mx-auto">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-5 md:gap-8">
              {platformStats.map((stat, idx) => (
                <div key={idx} className="text-center">
                  <div className="w-12 h-12 mx-auto bg-accent-100 rounded-xl flex items-center justify-center mb-4">
                    <i className={`${stat.icon} text-accent-600 text-xl`}></i>
                  </div>
                  <p className="text-3xl md:text-4xl font-bold text-foreground-950 font-heading">{stat.value}</p>
                  <p className="text-sm text-foreground-500 mt-1">{stat.label}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Brand story + Features */}
        <section className="bg-background-50 py-14 md:py-20">
          <div className="px-4 md:px-6 max-w-7xl mx-auto">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-10 md:gap-14">
              <div>
                <h2 className="text-2xl font-bold text-foreground-950 font-heading mb-4">品牌故事</h2>
                <p className="text-foreground-600 leading-relaxed">
                  古风漫画网的名字源于创始团队对东方传统文化的热爱。我们相信，韩国漫画中蕴含的东方美学基因，与中国古风文化有着深刻的共鸣。因此我们以"古风"为品牌基调，精心挑选那些在画风、故事、意境上具有古风韵味的韩漫佳作，为中国读者打造一个既有品质又有情怀的漫画阅读空间。
                </p>
                <p className="text-foreground-600 leading-relaxed mt-4">
                  我们的团队由资深漫画编辑、设计师和工程师组成，深耕漫画行业多年。我们不仅关注内容的品质，更注重用户的阅读体验——从流畅的下拉式阅读到清晰的分类导航，每一个细节都经过精心打磨。
                </p>
              </div>
              <div className="space-y-5">
                <div className="bg-background-100 rounded-2xl p-6">
                  <div className="flex items-center gap-3 mb-2">
                    <div className="w-10 h-10 bg-accent-100 rounded-xl flex items-center justify-center">
                      <i className="ri-book-open-line text-accent-600 text-lg"></i>
                    </div>
                    <h3 className="font-semibold text-foreground-900">海量资源</h3>
                  </div>
                  <p className="text-sm text-foreground-500">收录超过5000部韩国漫画，涵盖古风武侠、言情、玄幻、宫斗、搞笑、悬疑等多元类型，满足各类读者的阅读偏好。</p>
                </div>
                <div className="bg-background-100 rounded-2xl p-6">
                  <div className="flex items-center gap-3 mb-2">
                    <div className="w-10 h-10 bg-accent-100 rounded-xl flex items-center justify-center">
                      <i className="ri-flashlight-line text-accent-600 text-lg"></i>
                    </div>
                    <h3 className="font-semibold text-foreground-900">极速更新</h3>
                  </div>
                  <p className="text-sm text-foreground-500">实时同步韩网更新，热门作品实现同步连载，每周新增超过200话内容，追更零时差。</p>
                </div>
                <div className="bg-background-100 rounded-2xl p-6">
                  <div className="flex items-center gap-3 mb-2">
                    <div className="w-10 h-10 bg-accent-100 rounded-xl flex items-center justify-center">
                      <i className="ri-smartphone-line text-accent-600 text-lg"></i>
                    </div>
                    <h3 className="font-semibold text-foreground-900">全平台畅读</h3>
                  </div>
                  <p className="text-sm text-foreground-500">支持电脑、平板、手机全终端访问。下拉式阅读模式专为移动端深度优化，无需下载安装，随时随地流畅阅读。</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Milestones timeline */}
        <section className="bg-background-100 py-14 md:py-20">
          <div className="px-4 md:px-6 max-w-7xl mx-auto">
            <h2 className="text-2xl md:text-3xl font-bold text-foreground-950 font-heading text-center mb-12">
              发展<span className="text-accent-500">历程</span>
            </h2>
            <div className="relative max-w-3xl mx-auto">
              {/* Vertical line */}
              <div className="absolute left-6 md:left-1/2 top-0 bottom-0 w-px bg-background-300/60 -translate-x-1/2 hidden md:block" />
              <div className="absolute left-6 top-0 bottom-0 w-px bg-background-300/60 md:hidden" />

              <div className="space-y-8 md:space-y-12">
                {milestones.map((m, idx) => (
                  <div key={idx} className={`relative flex items-start gap-6 ${idx % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'}`}>
                    {/* Dot */}
                    <div className="absolute left-5 md:left-1/2 w-3 h-3 bg-accent-500 rounded-full -translate-x-1/2 mt-1.5 z-10 ring-4 ring-background-100" />
                    {/* Content */}
                    <div className={`md:w-1/2 ${idx % 2 === 0 ? 'md:pr-12 md:text-right' : 'md:pl-12'} ml-12 md:ml-0`}>
                      <span className="text-xs font-semibold text-accent-600 tracking-wider">{m.year}</span>
                      <h3 className="text-lg font-bold text-foreground-950 mt-1">{m.title}</h3>
                      <p className="text-sm text-foreground-500 mt-1.5 leading-relaxed">{m.desc}</p>
                    </div>
                    {/* Spacer for alternating layout */}
                    <div className="hidden md:block md:w-1/2" />
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Business cooperation + Contact form */}
        <section className="bg-background-50 py-14 md:py-20">
          <div className="px-4 md:px-6 max-w-7xl mx-auto">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-10 md:gap-14">
              {/* Left: Cooperation info */}
              <div>
                <h2 className="text-2xl md:text-3xl font-bold text-foreground-950 font-heading mb-6">
                  商务<span className="text-accent-500">合作</span>
                </h2>
                <p className="text-sm text-foreground-600 leading-relaxed mb-8">
                  古风漫画网欢迎各类商务合作洽谈，包括但不限于内容授权、广告投放、品牌联名、IP运营等合作模式。
                  请填写右侧表单或通过以下方式联系我们，我们的商务团队将在1-2个工作日内与您取得联系。
                </p>
                <div className="space-y-4">
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 bg-accent-100 rounded-xl flex items-center justify-center shrink-0">
                      <i className="ri-mail-line text-accent-600 text-lg"></i>
                    </div>
                    <div>
                      <p className="text-xs text-foreground-400">商务邮箱</p>
                      <p className="text-sm font-medium text-foreground-900">hello@xudongprc.com</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 bg-accent-100 rounded-xl flex items-center justify-center shrink-0">
                      <i className="ri-phone-line text-accent-600 text-lg"></i>
                    </div>
                    <div>
                      <p className="text-xs text-foreground-400">商务热线</p>
                      <p className="text-sm font-medium text-foreground-900">400-888-6667</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 bg-accent-100 rounded-xl flex items-center justify-center shrink-0">
                      <i className="ri-map-pin-line text-accent-600 text-lg"></i>
                    </div>
                    <div>
                      <p className="text-xs text-foreground-400">公司地址</p>
                      <p className="text-sm font-medium text-foreground-900 leading-relaxed">
                        浙江省杭州市西湖区文三路478号<br />华星时代广场15层 古风漫画网
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Right: Contact form */}
              <div>
                <div className="bg-background-100 rounded-2xl p-6 md:p-8">
                  <h3 className="text-lg font-bold text-foreground-950 font-heading mb-6">合作咨询</h3>
                  {formSuccess ? (
                    <div className="text-center py-10">
                      <div className="w-14 h-14 mx-auto bg-green-100 rounded-2xl flex items-center justify-center mb-4">
                        <i className="ri-check-line text-green-600 text-2xl"></i>
                      </div>
                      <p className="text-foreground-900 font-medium">提交成功！</p>
                      <p className="text-sm text-foreground-500 mt-2">感谢您的咨询，我们的商务团队将在1-2个工作日内与您联系。</p>
                    </div>
                  ) : (
                    <form onSubmit={handleSubmit}>
                      <div className="space-y-4">
                        <div>
                          <label className="block text-sm font-medium text-foreground-800 mb-1.5">
                            姓名 <span className="text-accent-500">*</span>
                          </label>
                          <input
                            type="text"
                            name="name"
                            required
                            className="w-full px-4 py-2.5 bg-background-50 border border-background-200 rounded-xl text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-accent-400 focus:ring-2 focus:ring-accent-100 transition-all"
                            placeholder="请输入您的姓名"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-foreground-800 mb-1.5">
                            公司名称
                          </label>
                          <input
                            type="text"
                            name="company"
                            className="w-full px-4 py-2.5 bg-background-50 border border-background-200 rounded-xl text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-accent-400 focus:ring-2 focus:ring-accent-100 transition-all"
                            placeholder="请输入您的公司名称"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-foreground-800 mb-1.5">
                            邮箱 <span className="text-accent-500">*</span>
                          </label>
                          <input
                            type="email"
                            name="email"
                            required
                            className="w-full px-4 py-2.5 bg-background-50 border border-background-200 rounded-xl text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-accent-400 focus:ring-2 focus:ring-accent-100 transition-all"
                            placeholder="请输入您的邮箱"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-foreground-800 mb-1.5">
                            合作意向
                          </label>
                          <select
                            name="cooperation_type"
                            className="w-full px-4 py-2.5 bg-background-50 border border-background-200 rounded-xl text-sm text-foreground-900 focus:outline-none focus:border-accent-400 focus:ring-2 focus:ring-accent-100 transition-all cursor-pointer"
                          >
                            <option value="">请选择合作类型</option>
                            <option value="content">内容授权合作</option>
                            <option value="advertising">广告投放合作</option>
                            <option value="brand">品牌联名合作</option>
                            <option value="ip">IP运营合作</option>
                            <option value="other">其他合作</option>
                          </select>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-foreground-800 mb-1.5">
                            留言内容 <span className="text-accent-500">*</span>
                          </label>
                          <textarea
                            name="message"
                            required
                            maxLength={500}
                            rows={4}
                            className="w-full px-4 py-2.5 bg-background-50 border border-background-200 rounded-xl text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-accent-400 focus:ring-2 focus:ring-accent-100 transition-all resize-none"
                            placeholder="请描述您的合作需求（500字以内）"
                          ></textarea>
                        </div>
                        {/* Honeypot */}
                        <input
                          type="text"
                          name="company_alt"
                          tabIndex={-1}
                          autoComplete="off"
                          aria-hidden="true"
                          readOnly
                          className="absolute opacity-0 pointer-events-none"
                        />
                        <button
                          type="submit"
                          className="w-full inline-flex items-center justify-center gap-2 px-6 py-3 bg-accent-500 text-background-50 rounded-full text-sm font-medium hover:bg-accent-600 transition-all whitespace-nowrap cursor-pointer"
                        >
                          提交咨询
                          <i className="ri-send-plane-line"></i>
                        </button>
                        {formError && (
                          <p className="text-sm text-red-500 text-center">{formError}</p>
                        )}
                      </div>
                    </form>
                  )}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Testimonials */}
        <section className="bg-background-100 py-14 md:py-20">
          <div className="px-4 md:px-6 max-w-7xl mx-auto">
            <h2 className="text-2xl md:text-3xl font-bold text-foreground-950 font-heading text-center mb-10">
              读者<span className="text-accent-500">口碑</span>
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-6">
              {testimonials.map((t) => (
                <div key={t.id} className="bg-background-50 rounded-2xl p-6 md:p-7 flex flex-col">
                  <div className="flex items-center gap-0.5 mb-4">
                    {Array.from({ length: t.rating }).map((_, i) => (
                      <i key={i} className="ri-star-fill text-accent-500 text-sm"></i>
                    ))}
                  </div>
                  <p className="text-sm text-foreground-700 leading-relaxed flex-1">&ldquo;{t.comment}&rdquo;</p>
                  <div className="flex items-center gap-3 mt-5 pt-5 border-t border-background-200/60">
                    <div className="w-10 h-10 rounded-full overflow-hidden shrink-0">
                      <img src={t.avatar} alt={t.name} className="w-full h-full object-cover" />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-foreground-900">{t.name}</p>
                      <p className="text-xs text-foreground-400">{t.date}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}